import { config } from "@/configuration/app/app-config"
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import { accessToken } from "@/configuration/token/accessToken"
import {
    ExternalAdminTransportOrderList,
    ExternalTransportOrderBundle, ExternalTransportOrderBundleList, ExternalTransportOrderList
} from "./model"
import {
    convertConfirmation as convertConfirmationToHubModel, convertExternalAdminTransportOrder, convertExternalTransportOrder,
    convertExternalTransportOrderBundle
} from "../../helper/transportOrderBundleConverter"
import {
    AdminShipment,
    FreightDiscriminator,
    Shipment,
    ShipmentConfirmation,
    TransportOrder
} from "@/model/transportorder/transportOrder"

const url = config.transportOrderApi.baseUrl

const GET_TRANSPORT_ORDER_BUNDLES_API_TAG = "GET_TRANSPORT_ORDER_BUNDLES"
const GET_TRANSPORT_ORDER_BUNDLE_API_TAG = "GET_TRANSPORT_ORDER_BUNDLE"
const GET_TRANSPORT_ORDERS_API_TAG = "GET_TRANSPORT_ORDERS_API_TAG"
const GET_INBOUND_TRANSPORT_ORDERS_API_TAG = "GET_INBOUND_TRANSPORT_ORDERS"

export type TransportOrderBundleFetchParams = {
    fromCreatedAt?: string
    toCreatedAt?: string
    fromEarliestPickupAt?: string
    toEarliestPickupAt?: string
    fromLatestDeliveryAt?: string
    toLatestDeliveryAt?: string
}

export type InboundTransportOrderFetchParams = {
    fromCreatedAt?: string
    toCreatedAt?: string
    fromPickupRequestedAt?: string
    toPickupRequestedAt?: string
    fromDeliveryEstimatedAt?: string
    toDeliveryEstimatedAt?: string
    fromArrivalEstimatedAt?: string
    toArrivalEstimatedAt?: string
}

export type AdminTransportOrderFetchParams = {
    identifier: string
}

export type TransportOrderByNumberFetchParams = {
    transportOrderNumber: string
    freightType: FreightDiscriminator
}

export const transportOrderHubApi = createApi({
    reducerPath: "transportOrderHubApi",
    tagTypes: [
        GET_TRANSPORT_ORDER_BUNDLES_API_TAG,
        GET_TRANSPORT_ORDER_BUNDLE_API_TAG,
        GET_TRANSPORT_ORDERS_API_TAG,
        GET_INBOUND_TRANSPORT_ORDERS_API_TAG,
    ],
    baseQuery: fetchBaseQuery({
        baseUrl: url,
        prepareHeaders: (headers) => {
            headers.set("authorization", `Bearer ${accessToken.getAccessToken()}`)
            headers.set("accept", "application/json")
            return headers
        },
    }),
    endpoints: (builder) => ({
        getTransportOrderBundle: builder.query<TransportOrder, { id: string }>({
            query: ({ id }) => `/transport-order-bundles/${id}?embed=transport_orders`,
            transformResponse: (response: unknown) => convertExternalTransportOrderBundle(response as ExternalTransportOrderBundle),
            providesTags: (_, __, arg) => [{ type: GET_TRANSPORT_ORDER_BUNDLE_API_TAG, id: arg.id }],
        }),
        getTransportOrderBundles: builder.query<TransportOrder[], TransportOrderBundleFetchParams>({
            query: (arg) => ({
                url: "/transport-order-bundles",
                params: {
                    embed: "transport_orders",
                    from_created_at: arg.fromCreatedAt,
                    to_created_at: arg.toCreatedAt,
                    from_earliest_pickup_at: arg.fromEarliestPickupAt,
                    to_earliest_pickup_at: arg.toEarliestPickupAt,
                    from_latest_delivery_at: arg.fromLatestDeliveryAt,
                    to_latest_delivery_at: arg.toLatestDeliveryAt,
                },
            }),
            transformResponse: (response: unknown) => {
                const bundleList = response as ExternalTransportOrderBundleList
                return bundleList.items.map(it => convertExternalTransportOrderBundle(it))
            },
            providesTags: () => [{ type: GET_TRANSPORT_ORDER_BUNDLES_API_TAG }],
        }),
        getInboundTransportOrders: builder.query<
            Shipment[],
            InboundTransportOrderFetchParams
        >({
            query: (arg) => ({
                url: "/crossdock/transport-orders",
                params: {
                    from_created_at: arg.fromCreatedAt,
                    to_created_at: arg.toCreatedAt,
                    from_pickup_requested_at: arg.fromPickupRequestedAt,
                    to_pickup_requested_at: arg.toPickupRequestedAt,
                    from_arrival_estimated_at: arg.fromArrivalEstimatedAt,
                    to_arrival_estimated_at: arg.toArrivalEstimatedAt,
                    from_delivery_estimated_at: arg.fromDeliveryEstimatedAt,
                    to_delivery_estimated_at: arg.toDeliveryEstimatedAt,
                },
            }),
            transformResponse: (response: unknown) => {
                const transportOrders = response as ExternalTransportOrderList
                return transportOrders.items.map(it => convertExternalTransportOrder(it, it.goods.type))
            },
            providesTags: () => [{ type: GET_INBOUND_TRANSPORT_ORDERS_API_TAG }],
        }),
        getTransportOrdersAsAdmin: builder.query<AdminShipment[], AdminTransportOrderFetchParams>({
            query: (arg) => `/rio-admin/transport-orders?identifier=${encodeURIComponent(arg.identifier)}`,
            transformResponse: (response: unknown) => {
                const transportOrders = response as ExternalAdminTransportOrderList
                return transportOrders.items.map(it => convertExternalAdminTransportOrder(it, it.goods.type))
            },
            providesTags: () => [{ type: GET_TRANSPORT_ORDERS_API_TAG }],
        }),
        getTransportOrdersByNumber: builder.query<Shipment[], TransportOrderByNumberFetchParams>({
            query: (arg) => ({
                url: "/transport-orders",
                params: {
                    external_id: arg.transportOrderNumber,
                },
            }),
            transformResponse: (response: unknown, _, arg) => {
                const transportOrders = response as ExternalTransportOrderList
                return transportOrders.items
                    .map(order => convertExternalTransportOrder(order, order.goods.type))
                    .filter(order => order.freight.type === arg.freightType)
            },
            providesTags: () => [{ type: GET_TRANSPORT_ORDERS_API_TAG }],
        }),
        postFreightForwarderResponse: builder.mutation<void, ShipmentConfirmation>({
            query: (arg: ShipmentConfirmation) => ({
                url: `/transport-orders/${arg.shipmentId}/freight-forwarder-responses`,
                method: "POST",
                body: convertConfirmationToHubModel(arg.confirmation),
                headers: {
                    "If-Match": `${arg.version}`,
                }
            }),
            invalidatesTags: (_, __, arg) => [
                { type: GET_TRANSPORT_ORDER_BUNDLES_API_TAG },
                { type: GET_TRANSPORT_ORDER_BUNDLE_API_TAG, id: arg.transportOrderId }
            ],
        }),
    }),
})

export const {
    useGetTransportOrderBundleQuery,
    useGetTransportOrderBundlesQuery,
    useGetInboundTransportOrdersQuery,
    usePostFreightForwarderResponseMutation,
    useLazyGetTransportOrdersAsAdminQuery,
    useLazyGetTransportOrdersByNumberQuery,
} = transportOrderHubApi
